import toast from 'react-hot-toast';
import { useMutation, useQuery } from 'react-query';

import { API_CTX } from '@/config';
import { queryClient } from '@/lib/react-query';
import { restoreURL } from '@/lib/axios';

import { APIResponse } from '@/types';

import { ITEMS_FOR_COMPANY, Item4AI, Item4Company } from '../types';

type SuccessCB = {
  onSuccess?: () => void;
};

/**
 * Convert ai module payload to backend needed structure:
 */
const addItem4AI = async ({ payload }: { payload: Item4AI }) => {
  const { ai_module_id, company_id, ...data } = payload;
  const url = `${API_CTX}/ai-modules/${ai_module_id}/companies/${company_id}/items`;
  const items = { items: [data] };
  await restoreURL().post(url, items);
};

export const useAddItem4AI = ({ onSuccess }: SuccessCB) => {
  return useMutation<void, Error, { payload: Item4AI }>({
    mutationKey: ITEMS_FOR_COMPANY,
    mutationFn: addItem4AI,
    onError: (error: Error) => {
      toast.error(`An error occurs: ${error.message}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries([ITEMS_FOR_COMPANY]);
      toast.success(`New AI item added successfully`);
      if (onSuccess) {
        onSuccess();
      }
    },
  });
};

/**
 * delete an item for compapny
 */
const deleteItem = async (
  ai_module_id: number,
  company_id: number,
  item_id: string,
  action: string
) => {
  const url = `${API_CTX}/ai-modules/${ai_module_id}/companies/${company_id}/items/${item_id}/actions/${action}`;
  await restoreURL().delete(url);
};

type DeleteParamsType = { aid: number; cid: number; item: string; act: string };

export const useDeleteItemfromAIModule = ({ onSuccess }: SuccessCB) => {
  return useMutation<void, Error, DeleteParamsType>(
    ({ aid, cid, item, act }) => deleteItem(aid, cid, item, act),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([ITEMS_FOR_COMPANY]);
        toast.success(`item in AI module deleted successfully`);

        if (onSuccess) {
          onSuccess();
        }
      },
      onError: (error: Error) => {
        toast.error(`An error occurs: ${error.message}`);
      },
    }
  );
};

/**
 * Query items being filtered out towards current AI module & company
 * @returns ai list
 */
export const getItems4Company = (
  ai_module_id: number,
  company_id: number
): Promise<APIResponse<Item4Company>> => {
  const url = `${API_CTX}/ai-modules/${ai_module_id}/companies/${company_id}/items`;
  return restoreURL().get(url);
};

export const useItems4Company = (aiID: number, companyID: number) => {
  return useQuery<APIResponse<Item4Company>, Error>([ITEMS_FOR_COMPANY], () =>
    getItems4Company(aiID, companyID)
  );
};
